import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Clock, CheckCircle, Eye } from 'lucide-react';
import { getMyHistory, type MyHistoryEntry } from '@/lib/api/endpoints/leaderboard';
import { queryKeys } from '@/lib/api/queryKeys';
import { Button } from '@/design-system/components/Button';
import { Badge } from '@/design-system/components/Badge';
import { EmptyState } from '@/design-system/components/EmptyState';
import { t } from '@/lib/i18n';
import styles from './HistoryPage.module.css';

const PAGE_SIZE = 15;

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? 1);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.me.history({ page, page_size: PAGE_SIZE }),
    queryFn: () => getMyHistory({ page, page_size: PAGE_SIZE }),
    staleTime: 30_000,
  });

  const entries = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.page_size) : 1;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('history.pageTitle')}</h1>
        <p className={styles.subtitle}>{t('history.subtitle')}</p>
      </div>

      {isLoading ? (
        <div className={styles.loading}>{t('common.loading')}</div>
      ) : entries.length === 0 ? (
        <EmptyState
          title={t('history.emptyTitle')}
          description={t('history.emptyDescription')}
        />
      ) : (
        <div className={styles.listWrap}>
          <div className={styles.listHeader}>
            <span>{t('history.colRound')}</span>
            <span>{t('history.colDate')}</span>
            <span>{t('history.colRank')}</span>
            <span>{t('history.colScore')}</span>
            <span>{t('history.colCorrect')}</span>
            <span></span>
          </div>
          <div className={styles.list}>
            {entries.map((entry) => (
              <HistoryRow
                key={`${entry.round_id}`}
                entry={entry}
                onView={() => navigate(`/rounds/${entry.round_id}/result`)}
              />
            ))}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setParams((p) => { p.set('page', String(page - 1)); return p; })}
          >
            {t('common.prev')}
          </Button>
          <span className={styles.pageInfo}>{t('common.page', { page, total: totalPages })}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setParams((p) => { p.set('page', String(page + 1)); return p; })}
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  );
}

function HistoryRow({
  entry,
  onView,
}: {
  entry: MyHistoryEntry;
  onView: () => void;
}) {
  const isSpectator = entry.role_in_round === 'spectator';

  return (
    <div className={styles.row}>
      {/* Tên màn chơi */}
      <div className={styles.rowName}>
        <span className={styles.roundName}>{entry.round_name}</span>
        {isSpectator && (
          <Badge tone="neutral">
            <Eye size={10} style={{ marginRight: 3 }} />
            {t('history.spectatorBadge')}
          </Badge>
        )}
      </div>

      {/* Ngày */}
      <div className={styles.rowDate}>
        <Clock size={13} className={styles.rowIcon} />
        {formatDate(entry.ended_at)}
      </div>

      {/* Hạng */}
      <div className={styles.rowRank}>
        {isSpectator ? (
          <span className={styles.spectatorLabel}>—</span>
        ) : entry.final_rank != null ? (
          <span className={`${styles.rank} ${entry.final_rank <= 3 ? styles.rankTop : ''}`}>
            {entry.final_rank <= 3 && ['🥇', '🥈', '🥉'][entry.final_rank - 1]}
            #{entry.final_rank}
          </span>
        ) : (
          <span className={styles.spectatorLabel}>—</span>
        )}
      </div>

      {/* Điểm */}
      <div className={styles.rowScore}>
        {isSpectator ? (
          <span className={styles.spectatorLabel}>—</span>
        ) : (
          <>
            <Trophy size={13} className={styles.rowIcon} />
            {entry.total_score.toLocaleString('vi-VN')}
          </>
        )}
      </div>

      {/* Câu đúng */}
      <div className={styles.rowCorrect}>
        {isSpectator ? (
          <span className={styles.spectatorLabel}>—</span>
        ) : (
          <>
            <CheckCircle size={13} className={styles.rowIconGreen} />
            {entry.correct_count} / {entry.total_questions}
          </>
        )}
      </div>

      {/* Action */}
      <div className={styles.rowAction}>
        <button className={styles.viewBtn} onClick={onView}>
          {t('history.viewResult')}
        </button>
      </div>
    </div>
  );
}
